// packages/core/src/learning/schools/behavioral-finance.ts
// ─────────────────────────────────────────────────────────────────────────
// SchoolExhibitEntry — authored to match boglehead-indexing.ts (gold standard).
// ID-bound fields (figures / featuredLessonIds / debateIds) left [] for the
// post-authoring cross-reference pass. Worldview named 'morgan-housel'
// (valid FigureId) — wire into `figures` during the cross-ref pass.
// ─────────────────────────────────────────────────────────────────────────

import type { SchoolExhibitEntry } from '../SchoolExhibitMeta';

export const BEHAVIORAL_FINANCE_EXHIBIT: SchoolExhibitEntry = {
	id: 'behavioral-finance',
	tagline:
		'Behavioral finance believes the spreadsheet is not the whole decision.',
	worldviewSummary:
		'Behavioral finance is paying attention to the gap between what people know and what people actually do under pressure. This lens protects you from overconfidence, panic, herd behavior, loss aversion, lifestyle creep, and money decisions made while emotional. It accepts a real tradeoff: psychology can explain a lot, but it can also become an excuse if you never build better systems. The blind spot is that behavior matters, but not every money problem is just mindset. Income, housing, family pressure, health, and starting point still matter.',
	coreBeliefs: [
		'Behavioral finance believes people are not calculators with bank accounts.',
		'Behavioral finance believes emotion can override knowledge when money feels personal.',
		'Behavioral finance believes the best plan is the one your actual behavior can follow.',
		'Behavioral finance believes overconfidence is expensive because it makes risk feel smaller than it is.',
		'Behavioral finance believes losses feel louder than gains, which can push people into bad timing.',
		'Behavioral finance believes systems beat willpower when stress is high.',
		'Behavioral finance believes money mistakes should be studied, not used as proof that someone is broken.',
	],
	warnsAgainst: [
		'Thinking financial knowledge automatically creates good financial behavior.',
		'Making permanent choices during panic, envy, boredom, shame, or hype.',
		'Following the crowd because uncertainty feels lonely.',
		'Using psychology as an excuse instead of building better habits and guardrails.',
	],
	helps: 'This lens helps adults who know what they want to do with money but keep getting pulled off track by emotion, pressure, impulse, or bad timing. It may not fit when someone uses behavior talk to ignore real constraints like low income, unstable work, family obligations, or high living costs.',
	figures: [],
	canon: [
		{
			title: 'Thinking, Fast and Slow',
			author: 'Daniel Kahneman',
			oneLineIdea:
				'Your fast reactions can feel true before your slower judgment has a chance to check them.',
		},
		{
			title: 'Prospect theory',
			author: 'Daniel Kahneman and Amos Tversky',
			oneLineIdea:
				'People often feel losses more strongly than similar gains, which can distort decisions.',
		},
		{
			title: 'Nudge',
			author: 'Richard Thaler and Cass Sunstein',
			oneLineIdea:
				'Decision design matters because default settings can quietly shape behavior.',
		},
		{
			title: 'The Psychology of Money',
			author: 'Morgan Housel',
			oneLineIdea:
				'Money behavior is shaped by personal history, ego, fear, luck, and what feels like enough.',
		},
		{
			title: 'The behavior gap',
			oneLineIdea:
				'The difference between a good plan and the results people get often comes from decisions made under emotion.',
		},
	],
	featuredLessonIds: [],
	nativeModuleId: 'school-behavioral-finance',
	debateIds: [],
	applyActions: [
		{
			label: 'Look at your Dashboard before making an emotional money move',
			action: {
				type: 'openDashboard',
				label: 'Open your Dashboard',
			},
		},
		{
			label: 'Test the future cost of an impulse decision',
			action: {
				type: 'openSimulator',
				label: 'Simulate the decision',
				scenarioType: 'custom',
			},
		},
		{
			label: 'Check whether your savings rate matches your intentions',
			action: {
				type: 'analyzeSavingsRate',
				label: 'Check your savings rate',
			},
		},
		{
			label: 'Write your rules before pressure shows up',
			action: {
				type: 'noToolAction',
				label: 'Write your money rules',
				prompt: 'Write three money decisions you tend to make emotionally, then write the rule you want to follow before each one.',
			},
		},
	],
	marketingHooks: [
		{
			line: 'Most money mistakes are not math mistakes. They are pressure mistakes.',
			kind: 'hook',
		},
		{
			line: 'The hidden cost is trusting your emotions only when money gets loud.',
			kind: 'hook',
		},
		{
			line: 'What money decision looked logical at the time but was really emotion in disguise?',
			kind: 'interview-question',
		},
		{
			line: 'When did you learn that knowing better and doing better are not the same thing?',
			kind: 'interview-question',
		},
		{
			line: 'Nisk teaches the behavior gap between what people know and what they actually do.',
			kind: 'kickstarter-beat',
		},
		{
			line: 'Financial education should teach panic, envy, confidence, shame, and timing, not just formulas.',
			kind: 'kickstarter-beat',
		},
		{
			line: 'Show your audience how one emotional decision can quietly change a future timeline.',
			kind: 'creator-prompt',
		},
		{
			line: 'Ask people what money rule they wish they had written before pressure showed up.',
			kind: 'creator-prompt',
		},
	],
	revision: 1,
};
